/**
 * F-013 — Slingshot power meter.
 *
 * Shows a horizontal bar at the bottom of the screen filling 0..100% as
 * the user drags. Fades out when no aim is active. Pure DOM (CSS
 * transform width) for legibility on every device.
 */

export interface PowerMeter {
  element: HTMLDivElement;
  setPower(power01: number): void;
  setVisible(visible: boolean): void;
  detach(): void;
}

export function createPowerMeter(host: HTMLElement): PowerMeter {
  const element = document.createElement("div");
  element.dataset["testid"] = "power-meter";
  // bottom-24 lifts the bar above the tier-ladder strip at bottom-4
  // so the gauge never collides with the goal panel.
  // Mobile-first: narrower bar at <640px so it doesn't push to the
  // edge of small screens.
  element.className =
    "pointer-events-none absolute bottom-16 left-1/2 -translate-x-1/2 h-1.5 w-44 overflow-hidden rounded-full bg-cosmic-mid/60 opacity-0 transition-opacity duration-200 sm:bottom-24 sm:h-2 sm:w-64";

  const fill = document.createElement("div");
  fill.dataset["testid"] = "power-meter-fill";
  fill.className =
    "h-full origin-left bg-gradient-to-r from-cosmic-glow via-cosmic-star to-rose-300";
  fill.style.transform = "scaleX(0)";

  element.append(fill);
  host.appendChild(element);

  const setPower = (power01: number): void => {
    const clamped = Math.max(0, Math.min(1, power01));
    fill.style.transform = `scaleX(${clamped})`;
  };

  const setVisible = (visible: boolean): void => {
    element.style.opacity = visible ? "1" : "0";
  };

  const detach = (): void => {
    element.remove();
  };

  return { element, setPower, setVisible, detach };
}
